import { useSelector } from "react-redux";

import { useGetDatasetStatsQueries } from "@/api/dataset/useDatasetAPI";
import {
  DEFAULT_LIMIT_THRESHOLD,
  LIMIT_THRESHOLD_BLOCK
} from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { selectInputRowCounts } from "@/store/selectors/spatialSelectors";
import {
  DatasetFilter,
  DatasetStatsCountStatusENUM,
  DatasetStatsResponse,
  H3Configuration
} from "@/types/dataset";

interface UseSpatialInputRowsLimitProps {
  /** Datasets to measure via `/stats` (empty = accumulator-only check). */
  statsTargets?: DatasetStatsTarget[];
  /** Accumulator ids excluded from the base total (replaced by a target). */
  excludeDatasetIds?: string[];
  /** Disable the `/stats` checks (e.g. catchment / analysis result). */
  enabled?: boolean;
  /** Optional fixture override for onboarding flows. */
  meta?: {
    isOnboarding?: boolean;
    onboardingMockData?: DatasetStatsResponse;
  };
}

/** A dataset to measure via `/stats` (filters/aggregation included). */
export interface DatasetStatsTarget {
  datasetId: string;
  filters?: DatasetFilter[];
  h3Configuration?: H3Configuration | null;
  group_by?: string;
  /** Stored count to use when `/stats` is NOT_AVAILABLE (H3/Geohash), so rows
   *  don't drop to 0. */
  fallbackDatasetId?: string;
}
/**
 * Single source of truth for the spatial-analysis total-input-rows limit.
 * Total = baseTotal (already-selected datasets) + statsTotal (targets measured
 * via `/stats`, excluded from the base so they aren't double-counted).
 */
export const useSpatialInputRowsLimit = ({
  statsTargets = [],
  excludeDatasetIds = [],
  enabled = true,
  meta
}: UseSpatialInputRowsLimitProps = {}) => {
  const inputRowCounts = useSelector(selectInputRowCounts);
  const isOnboarding = meta?.isOnboarding === true;

  // baseTotal = rows of already-selected datasets. First drop the excluded ids
  // (those re-measured via `/stats`, added back through statsTotal — avoids
  // double-count), then sum. Skip rebuilding when nothing is excluded.
  const excluded = new Set(excludeDatasetIds);
  const filteredInputRowCounts = excluded.size
    ? Object.fromEntries(
        Object.entries(inputRowCounts).filter(([id]) => !excluded.has(id))
      )
    : inputRowCounts;

  const baseTotal = Object.values(filteredInputRowCounts).reduce(
    (sum, count) => sum + (count ?? 0),
    0
  );

  // Stream-side check for `onStreamComplete` (H3 can't be measured by `/stats`
  // pre-flight). baseTotal + streamed rows > limit → toast, return true.
  const isStreamInputRowLimitExceeded = ({
    streamedRows
  }: {
    streamedRows: number;
  }) => {
    if (baseTotal + streamedRows <= DEFAULT_LIMIT_THRESHOLD) return false;
    showToast({
      type: EToastType.ERROR,
      title: MESSAGE.LIMIT_THRESHOLD_WARNING.TITLE,
      message: MESSAGE.LIMIT_THRESHOLD_WARNING.MESSAGE(
        DEFAULT_LIMIT_THRESHOLD,
        "dataset"
      )
    });
    return true;
  };

  const results = useGetDatasetStatsQueries({
    statsTargets,
    enabled: isOnboarding ? false : enabled,
    meta
  });

  if (isOnboarding) {
    return {
      limit: DEFAULT_LIMIT_THRESHOLD,
      isOverLimit: false,
      isCountAvailable: true,
      isFetchingStatusDataset: false,
      isStreamInputRowLimitExceeded
    };
  }

  // Per target: use row_count (EXACT/EXCEEDS); else NOT_AVAILABLE/unresolved →
  // fall back to the stored count so its rows don't drop to 0.
  const statsTotal = results.reduce((sum, result, index) => {
    const data = result.data;
    if (
      data &&
      data.count_status !== DatasetStatsCountStatusENUM.NOT_AVAILABLE
    ) {
      return sum + data.row_count;
    }
    const fallbackId = statsTargets[index]?.fallbackDatasetId;
    return sum + (fallbackId ? (inputRowCounts[fallbackId] ?? 0) : 0);
  }, 0);

  // Any `/stats` in flight → caller holds the action button.
  const isFetchingStatusDataset = results.some((result) => result.isFetching);

  // False if any target is NOT_AVAILABLE (can't measure pre-save); callers then
  // fall back to the "dirty" heuristic (H3 case).
  const isCountAvailable = results.every(
    (result) =>
      result.data?.count_status !== DatasetStatsCountStatusENUM.NOT_AVAILABLE
  );

  // Over once the total reaches the block threshold (±5% below the limit).
  const isOverLimit = baseTotal + statsTotal >= LIMIT_THRESHOLD_BLOCK;

  return {
    limit: DEFAULT_LIMIT_THRESHOLD,
    isOverLimit,
    isCountAvailable,
    isFetchingStatusDataset,
    isStreamInputRowLimitExceeded
  };
};
